// Labels for the intake register (/intake-requests), its Drawer and the
// Approvals inbox, and for the older /intake-review pages kept for one
// release. Source values come from the backend: stored `source` ("in_app",
// "portal", "chat") or a label derived in backend/routes/intake_review.py.

export const SOURCE_LABEL = {
  in_app: 'In-app wizard',
  portal: 'Intake portal',
  chat: 'Intake chat',
  ado_import: 'ADO import',
  backlog: 'Backlog',
  unspecified: 'No source',
};

export const STATE_LABEL = {
  CAPTURED: 'Received',
  INTAKE_COMPLETE: 'Intake complete',
  REFINED: 'Refined',
  SCORED: 'Scored',
  SIZED: 'Sized',
  PLANNED: 'Planned',
  IN_PROGRESS: 'In progress',
  DONE: 'Done',
  NEEDS_INFO: 'Needs info',
  NEEDS_REWORK: 'Needs rework',
  BLOCKED: 'Blocked',
  DEFERRED: 'Deferred',
  DISCARDED: 'Discarded',
};

export const STATE_TONE = {
  CAPTURED: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  NEEDS_INFO: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  NEEDS_REWORK: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  BLOCKED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  DONE: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  DEFERRED: 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white/70',
  DISCARDED: 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white/70',
};
export const DEFAULT_STATE_TONE = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';

export const sourceLabel = (s) => SOURCE_LABEL[s] || s || SOURCE_LABEL.unspecified;
export const stateLabel = (s) => STATE_LABEL[s] || s || '';

export function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function requesterText(requester) {
  if (!requester) return '';
  return requester.name || requester.email || '';
}

// The register lists intakes only (ruled 2026-10-21): these are its sources.
export const INTAKE_SOURCES = ['portal', 'in_app', 'chat'];

// Status filter groups, as STATUS_GROUPS in backend/routes/intake_review.py.
export const STATUS_GROUP_LABEL = {
  in_flight: 'In flight',
  closed: 'Closed',
};

// Approval statuses. "discarded" is what the backend stores for Reject.
export const APPROVAL_LABEL = {
  pending: 'Awaiting',
  approved: 'Approved',
  changes_requested: 'Changes requested',
  deferred: 'Deferred',
  discarded: 'Rejected',
};

export const APPROVAL_TONE = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  changes_requested: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  deferred: 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white/70',
  discarded: 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white/70',
};

export const approvalLabel = (s) => APPROVAL_LABEL[s] || s || '';

// "G1: Approved", or '' when the item has no G1.
export function g1Text(status) {
  return status ? `G1: ${approvalLabel(status)}` : '';
}

// The item's kind, from the fields each create path sets.
export function typeLabel(item) {
  if (!item) return '';
  if (item.request_type === 'ai_use_case') return 'AI use case';
  const t = item.initiative_type || item.ado_type;
  if (!t) return '';
  return String(t).charAt(0).toUpperCase() + String(t).slice(1).toLowerCase();
}

// States in which the submitter has to act. Until submitters can edit and
// resubmit (follow-up story), they reply to the reviewer (ruled 2026-10-21).
export const SUBMITTER_ACTION_STATES = ['NEEDS_INFO', 'NEEDS_REWORK'];
// Shown with the reviewer's note on changes requested.
export const SUBMITTER_ACTION_HINT = 'Reply to the reviewer to continue this request.';
// Shown when the item waits on the submitter but carries no reviewer note.
export const SUBMITTER_NO_NOTE_HINT = 'The team needs more information. Contact the intake team.';
// Register rows do not carry the note, so they only point to the Drawer.
export const SUBMITTER_ROW_HINT = 'Open the request to see what the team needs.';
